import { controls } from "./controls.js";
import { renderControls } from "./controls-panel.js";
import { asConvertOptions, type ToolOptions } from "./options.js";
import type { ConvertOptions } from "@kensio/pinyinjs";
import { browserStorage, loadOptions, saveOptions } from "./settings-store.js";

/**
 * The settings, from the last visit to the next.
 *
 * Loading them, drawing the panel, saving a change and telling the tool to
 * re-convert are one job rather than four: every one of them happens because
 * of one of the others, and none of them is interesting to the conversion
 * itself, which only ever wants the current `ConvertOptions`.
 */
export class ToolSettings {
  readonly #storage = browserStorage();

  #options: ToolOptions;

  /**
   * Draw the panel into a container, and report every change.
   *
   * `idPrefix` namespaces the radios' ids and names, so that two tools on one
   * page could not collide.
   *
   * `fromUrl` wins over what the last visit stored, and both over the
   * defaults. A link somebody was sent says what that link is for, and it
   * would be a strange thing to open and find showing something else — but it
   * only speaks for the settings it names, so the rest still come from this
   * device.
   */
  constructor(
    panel: Element,
    idPrefix: string,
    fromUrl: Partial<ToolOptions>,
    onChange: () => void,
  ) {
    this.#options = { ...loadOptions(this.#storage), ...fromUrl };

    renderControls(panel, controls, this.#options, idPrefix, (changed) => {
      this.#options = { ...this.#options, ...changed };
      saveOptions(this.#storage, this.#options);
      onChange();
    });
  }

  /** The settings as the conversion wants them. */
  get convertOptions(): ConvertOptions {
    return asConvertOptions(this.#options);
  }

  /** The settings in full, which is what the address bar is written from. */
  get options(): ToolOptions {
    return this.#options;
  }
}
